import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  FetchPersonaInquiry,
  SetPersonaClientCompletedInquiry,
  SetPersonaSessionToken,
} from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { PersonaInquiry } from 'src/interfaces/persona'
import Persona, { Client as PersonaClient } from 'persona'
import { captureSentryError } from 'src/utils/sentry'
import { InquiryError } from 'persona/dist/lib/interfaces'
import { PERSONA_ERROR_CODES, PersonaInquiryType } from 'src/constants/persona'

type UsePersonaOptions = {
  inquiryType?: PersonaInquiryType
  onError?: (error: InquiryError) => void
}

export const usePersona = (options: UsePersonaOptions = {}) => {
  const dispatch = useDispatch()

  const personaClient = useRef<PersonaClient | null>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const [isClientReady, setIsClientReady] = useState<boolean>(false)
  const [hasInitError, setHasInitError] = useState<boolean>(false)

  const handlePersonaCancel = useCallback(
    async (sessionToken: string | undefined, stepId: string) => {
      if (!sessionToken) {
        return
      }

      try {
        await createAsyncAction(dispatch, SetPersonaSessionToken.request({ sessionToken, stepId }))
      } catch (error) {
        captureSentryError(error, {
          message: '[handlePersonaCancel]',
        })
      }
    },
    [dispatch],
  )

  const handlePersonaError = useCallback(
    (error: InquiryError, stepId: string) => {
      captureSentryError(error, {
        message: '[handlePersonaError]',
      })

      if (PERSONA_ERROR_CODES.includes(error.code)) {
        dispatch(SetPersonaClientCompletedInquiry.request({ stepId }))
      }

      if (typeof options.onError === 'function') {
        options.onError(error)
      }
    },
    [dispatch, options.onError],
  )

  const handlePersonaComplete = useCallback(
    async (stepId: string) => {
      setIsPolling(true)

      try {
        await createAsyncAction(dispatch, SetPersonaClientCompletedInquiry.request({ stepId }))
      } catch (error) {
        showToast('Failed to complete persona, please try again later', {
          variant: 'error',
        })
      } finally {
        setIsPolling(false)
      }
    },
    [dispatch],
  )

  useEffect(() => {
    return () => {
      if (!personaClient.current) {
        return
      }

      personaClient.current.destroy()
      personaClient.current = null
    }
  }, [])

  const initPersona = useCallback(
    async (stepId: string) => {
      if (personaClient.current) {
        personaClient.current.destroy()
        personaClient.current = null
      }

      setIsClientReady(false)
      setHasInitError(false)
      setIsLoading(true)

      try {
        const personaInquiry: PersonaInquiry | undefined = await createAsyncAction(
          dispatch,
          FetchPersonaInquiry.request({
            stepId,
            inquiryType: options.inquiryType,
          }),
        )

        if (!personaInquiry) {
          return
        }

        personaClient.current = new Persona.Client({
          environmentId: process.env.REACT_APP_PERSONA_ENV_ID,
          inquiryId: personaInquiry.id,
          sessionToken: personaInquiry.sessionToken,
          onCancel: ({ sessionToken }) => {
            return handlePersonaCancel(sessionToken, stepId)
          },
          onError: (error) => {
            return handlePersonaError(error, stepId)
          },
          onComplete: () => {
            return handlePersonaComplete(stepId)
          },
        })

        setIsClientReady(true)
      } catch (error) {
        setHasInitError(true)
        showToast('Failed to load persona, please try again later', {
          variant: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, options.inquiryType, handlePersonaCancel, handlePersonaError, handlePersonaComplete],
  )

  const openPersona = useCallback(() => {
    return personaClient.current?.open()
  }, [])

  const startPersona = useCallback(
    async (stepId: string) => {
      await initPersona(stepId)

      return personaClient.current?.open()
    },
    [initPersona],
  )

  return {
    isPersonaLoading: isLoading || isPolling,
    isClientReady,
    hasInitError,
    initPersona,
    openPersona,
    startPersona,
  }
}
